import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Save, CheckCircle2, Info, Layers, AlertTriangle, Download, Video, ClipboardList, Smile, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/axios';
import { useAuthStore, ROL } from '@/store/auth.store';
import { cn } from '@/lib/utils';
import {
  TOGGLEABLE, ALWAYS_ON_MODULES, HERRAMIENTAS_DENTALES, ALL_ENABLED,
  ANTECEDENTES_TOGGLEABLE, ALL_ANTECEDENTES_ENABLED, PLANTILLA_EXPLORACION_PRESET_NOMBRES,
} from '@/lib/perfilClinicaPresets';
import { ExportacionMasivaPanel } from './ExportacionMasivaPanel';
import { getErrorMessage } from '@/lib/errors';
import { TareasConfigPanel } from './TareasConfigPanel';

// ── Componente toggle ─────────────────────────────────────────────────────────

function Toggle({ enabled, onChange, disabled }: {
  enabled: boolean;
  onChange: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={enabled}
      disabled={disabled}
      onClick={onChange}
      className={cn(
        'w-10 h-5 rounded-full relative transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:ring-offset-1 flex-shrink-0',
        enabled ? 'bg-primary-500' : 'bg-gray-200',
        disabled && 'cursor-default opacity-60'
      )}
    >
      <span
        className={cn(
          'absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform',
          enabled ? 'translate-x-5' : 'translate-x-0'
        )}
      />
    </button>
  );
}

// ── Cobro online teleconsultas ────────────────────────────────────────────────

function TeleconsultaOnlineToggle() {
  const qc = useQueryClient();

  const { data } = useQuery({
    queryKey: ['stripe-status'],
    queryFn:  () => api.get('/api/suscripcion/stripe/status').then(r => r.data),
    staleTime: 60_000,
  });

  const toggleMut = useMutation({
    mutationFn: (enabled: boolean) =>
      api.patch('/api/suscripcion/teleconsulta-online', { enabled }).then(r => r.data),
    onSuccess: (res) => {
      toast.success(res.cobrarTeleconsultaOnline ? 'Cobro online activado' : 'Cobro online desactivado');
      qc.invalidateQueries({ queryKey: ['stripe-status'] });
    },
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al actualizar la configuración')),
  });

  const cobrarOnline = data?.cobrarTeleconsultaOnline ?? false;

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-6">
      <div className="flex items-center gap-2 mb-4">
        <Video size={18} className="text-primary-500" />
        <h3 className="font-semibold text-text-primary">Teleconsultas</h3>
      </div>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-text-primary">Cobro online</p>
          <p className="text-sm text-text-secondary mt-0.5">
            Los pacientes pagan con tarjeta antes de entrar a la videollamada
          </p>
        </div>
        <Toggle
          enabled={cobrarOnline}
          onChange={() => toggleMut.mutate(!cobrarOnline)}
          disabled={toggleMut.isPending}
        />
      </div>
    </div>
  );
}

// ── Plantillas sugeridas por perfil de clínica ─────────────────────────────────

function PlantillasSugeridasCard({ perfilTipo }: { perfilTipo: string }) {
  const qc = useQueryClient();
  const nombres = PLANTILLA_EXPLORACION_PRESET_NOMBRES[perfilTipo] ?? [];

  const seedMut = useMutation({
    mutationFn: () => api.post('/api/formularios-dinamicos/plantillas/seed-sugeridas').then(r => r.data),
    onSuccess: (res: { creadas: string[]; omitidas: string[] }) => {
      // Dos query keys independientes para el mismo recurso: 'plantillas-exploracion' (CRUD
      // admin, esta página) y 'plantillas-exploracion-activas' (lista activa que consume
      // ExploracionesDinamicasTab dentro de la Consulta) — invalidar ambas.
      qc.invalidateQueries({ queryKey: ['plantillas-exploracion'] });
      qc.invalidateQueries({ queryKey: ['plantillas-exploracion-activas'] });
      if (res.creadas.length > 0) {
        toast.success(`Se crearon ${res.creadas.length} plantilla(s): ${res.creadas.join(', ')}`);
      } else {
        toast.info('No hay plantillas nuevas por crear — ya existen todas.');
      }
    },
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al crear las plantillas sugeridas')),
  });

  if (nombres.length === 0) return null;

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-6">
      <div className="flex items-center gap-2 mb-1">
        <Sparkles size={18} className="text-primary-500" />
        <h3 className="font-semibold text-text-primary">Plantillas sugeridas para tu perfil</h3>
      </div>
      <p className="text-sm text-text-secondary mb-4">
        Crea de un clic los formularios clínicos sugeridos para tu especialidad: {nombres.join(', ')}.
        Podrás editarlos o desactivarlos después, igual que uno creado manualmente.
      </p>
      <button
        type="button"
        onClick={() => seedMut.mutate()}
        disabled={seedMut.isPending}
        className="flex items-center gap-1.5 text-xs font-medium text-primary-600 hover:text-primary-700 disabled:opacity-60 transition-colors"
      >
        <Sparkles size={13} />
        {seedMut.isPending ? 'Creando...' : 'Crear plantillas sugeridas'}
      </button>
    </div>
  );
}

// ── Página ────────────────────────────────────────────────────────────────────

function BannerCancelacionPendiente({ clinicaId }: { clinicaId: string }) {
  const { data } = useQuery({
    queryKey: ['ciclo-vida', clinicaId],
    queryFn: () => api.get(`/api/ciclo-vida/clinicas/${clinicaId}`).then(r => r.data),
    staleTime: 5 * 60 * 1000,
  });

  if (data?.estadoCiclo !== 'CANCELACION_PENDIENTE' || !data?.fechaCancelacionDefinitiva) return null;

  const diasRestantes = Math.max(
    0,
    Math.ceil((new Date(data.fechaCancelacionDefinitiva).getTime() - Date.now()) / 86_400_000),
  );

  return (
    <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl px-5 py-4">
      <AlertTriangle size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-amber-800">Cancelación programada</p>
        <p className="text-sm text-amber-700 mt-0.5">
          Tu clínica será archivada en <strong>{diasRestantes} día{diasRestantes !== 1 ? 's' : ''}</strong>
          {' '}({new Date(data.fechaCancelacionDefinitiva).toLocaleDateString('es-MX', { day: 'numeric', month: 'long', year: 'numeric' })}).
          Descarga tus expedientes antes de esa fecha para conservarlos.
        </p>
      </div>
    </div>
  );
}

export function ConfiguracionPage() {
  const { usuario } = useAuthStore();
  const qc = useQueryClient();
  const canWrite = usuario?.rolId === ROL.SUPER_ADMIN
    || usuario?.rolId === ROL.ADMIN_CLINICA
    || usuario?.rolId === ROL.MEDICO_ADMIN;
  const canExport = usuario?.rolId === ROL.ADMIN_CLINICA || usuario?.rolId === ROL.MEDICO_ADMIN;

  // ── Config general ────────────────────────────────────────────────────────
  const { data: config } = useQuery({
    queryKey: ['configuracion'],
    queryFn: () => api.get('/api/configuracion').then(r => r.data),
  });

  const [form, setForm] = useState({
    perfilTipo: 'medica_general',
    modulosHabilitadosJson: { ...ALL_ENABLED } as Record<string, boolean>,
    configuracionJson: { antecedentesSecciones: { ...ALL_ANTECEDENTES_ENABLED } as Record<string, boolean> },
  });

  useEffect(() => {
    if (config === undefined) return;
    const modulos = config?.modulosHabilitadosJson as Record<string, boolean> | null;
    const antecedentesSecciones = (config?.configuracionJson as any)?.antecedentesSecciones as Record<string, boolean> | undefined;
    setForm({
      perfilTipo: config?.perfilTipo ?? 'medica_general',
      modulosHabilitadosJson: modulos && Object.keys(modulos).length > 0
        ? modulos
        : { ...ALL_ENABLED },
      configuracionJson: {
        antecedentesSecciones: antecedentesSecciones && Object.keys(antecedentesSecciones).length > 0
          ? antecedentesSecciones
          : { ...ALL_ANTECEDENTES_ENABLED },
      },
    });
  }, [config]);

  const saveConfig = useMutation({
    mutationFn: (dto: typeof form) => api.put('/api/configuracion', dto).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['configuracion'] });
      toast.success('Configuración guardada');
    },
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al guardar configuración')),
  });

  function toggleModulo(key: string) {
    if (!canWrite) return;
    setForm(f => ({
      ...f,
      modulosHabilitadosJson: { ...f.modulosHabilitadosJson, [key]: !f.modulosHabilitadosJson[key] },
    }));
  }

  function enableAll() {
    if (!canWrite) return;
    setForm(f => ({ ...f, modulosHabilitadosJson: { ...ALL_ENABLED } }));
  }

  function toggleAntecedenteSeccion(key: string) {
    if (!canWrite) return;
    setForm(f => ({
      ...f,
      configuracionJson: {
        ...f.configuracionJson,
        antecedentesSecciones: { ...f.configuracionJson.antecedentesSecciones, [key]: !f.configuracionJson.antecedentesSecciones[key] },
      },
    }));
  }

  function enableAllAntecedentes() {
    if (!canWrite) return;
    setForm(f => ({ ...f, configuracionJson: { antecedentesSecciones: { ...ALL_ANTECEDENTES_ENABLED } } }));
  }

  function enableAllDentales() {
    if (!canWrite) return;
    setForm(f => ({
      ...f,
      modulosHabilitadosJson: {
        ...f.modulosHabilitadosJson,
        ...Object.fromEntries(HERRAMIENTAS_DENTALES.map(h => [h.id, true])),
      },
    }));
  }

  const enabledCount = TOGGLEABLE.filter(m => !!form.modulosHabilitadosJson[m.id]).length;
  const antecedentesEnabledCount = ANTECEDENTES_TOGGLEABLE.filter(s => !!form.configuracionJson.antecedentesSecciones[s.id]).length;
  const mostrarHerramientasDentales = form.perfilTipo === 'dental' || form.perfilTipo === 'multidisciplinaria';
  const dentalesEnabledCount = HERRAMIENTAS_DENTALES.filter(h => !!form.modulosHabilitadosJson[h.id]).length;

  return (
    <div className="space-y-6">

      {/* Banner cancelación pendiente */}
      {usuario?.clinicaId && (
        <BannerCancelacionPendiente clinicaId={usuario.clinicaId} />
      )}

      {/* Header */}
      <div className="flex items-center justify-end">
        {canWrite && (
          <button
            onClick={() => saveConfig.mutate(form)}
            disabled={saveConfig.isPending}
            className="flex items-center gap-2 bg-primary-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-primary-700 disabled:opacity-60 transition-colors"
          >
            <Save size={15} />
            {saveConfig.isPending ? 'Guardando...' : 'Guardar cambios'}
          </button>
        )}
      </div>

      {/* Herramientas dentales — solo relevante si el perfil es Dental o Multidisciplinaria */}
      {mostrarHerramientasDentales && (
        <div className="bg-white rounded-xl border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2">
              <Smile size={18} className="text-primary-500" />
              <h3 className="font-semibold text-text-primary">Herramientas dentales</h3>
              <span className="text-xs font-medium text-text-secondary bg-gray-100 rounded-full px-2 py-0.5">
                {dentalesEnabledCount} / {HERRAMIENTAS_DENTALES.length}
              </span>
            </div>
            {canWrite && (
              <button
                type="button"
                onClick={enableAllDentales}
                className="flex items-center gap-1.5 text-xs font-medium text-primary-600 hover:text-primary-700 transition-colors"
              >
                <CheckCircle2 size={13} />
                Activar todo
              </button>
            )}
          </div>
          <p className="text-sm text-text-secondary mb-4">
            Actívalas o desactívalas manualmente dentro de la pestaña "Dental" del expediente del paciente.
          </p>
          <div className="divide-y divide-gray-50">
            {HERRAMIENTAS_DENTALES.map(herramienta => {
              const enabled = !!form.modulosHabilitadosJson[herramienta.id];
              const Icon = herramienta.icon;
              return (
                <div key={herramienta.id} className="flex items-center gap-4 py-3 first:pt-0 last:pb-0">
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 bg-primary-50">
                    <Icon size={17} className="text-primary-500" />
                  </div>
                  <p className="flex-1 min-w-0 text-sm font-medium text-text-primary">{herramienta.label}</p>
                  <Toggle
                    enabled={enabled}
                    onChange={() => toggleModulo(herramienta.id)}
                    disabled={!canWrite}
                  />
                </div>
              );
            })}
          </div>
        </div>
      )}


      {/* Módulos habilitados */}
      <div className="bg-white rounded-xl border border-gray-100 p-6">

        {/* Cabecera de la sección */}
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <Layers size={18} className="text-primary-500" />
            <h3 className="font-semibold text-text-primary">Módulos habilitados</h3>
            <span className="text-xs font-medium text-text-secondary bg-gray-100 rounded-full px-2 py-0.5">
              {enabledCount} / {TOGGLEABLE.length}
            </span>
          </div>
          {canWrite && (
            <button
              type="button"
              onClick={enableAll}
              className="flex items-center gap-1.5 text-xs font-medium text-primary-600 hover:text-primary-700 transition-colors"
            >
              <CheckCircle2 size={13} />
              Activar todo
            </button>
          )}
        </div>

        {/* Lista de módulos */}
        <div className="divide-y divide-gray-50">
          {TOGGLEABLE.map(module => {
            const enabled = !!form.modulosHabilitadosJson[module.id];
            const Icon = module.icon;
            const sectionLabels = module.sections.map(s => s.label).join(' · ');

            return (
              <div key={module.id} className="flex items-center gap-4 py-3.5 first:pt-0 last:pb-0">
                {/* Icono con color del módulo */}
                <div
                  className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: `${module.color}18` }}
                >
                  <Icon size={17} style={{ color: module.color }} />
                </div>

                {/* Nombre y secciones */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-text-primary">{module.label}</p>
                  <p className="text-xs text-text-secondary mt-0.5 truncate">{sectionLabels}</p>
                </div>

                {/* Toggle */}
                <Toggle
                  enabled={enabled}
                  onChange={() => toggleModulo(module.id)}
                  disabled={!canWrite}
                />
              </div>
            );
          })}
        </div>

        {/* Módulos siempre activos */}
        <div className="mt-5 pt-4 border-t border-gray-50">
          <p className="text-[11px] font-semibold text-text-secondary uppercase tracking-wide mb-3">
            Siempre activos
          </p>
          <div className="flex flex-wrap gap-2">
            {ALWAYS_ON_MODULES.map(module => {
              const Icon = module.icon;
              return (
                <div
                  key={module.id}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-gray-100 bg-gray-50"
                >
                  <Icon size={12} className="text-gray-400" />
                  <span className="text-xs text-text-secondary font-medium">{module.label}</span>
                </div>
              );
            })}
          </div>
          <div className="flex items-start gap-1.5 mt-3">
            <Info size={12} className="text-gray-400 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-text-secondary">
              Estos módulos no pueden deshabilitarse para mantener el acceso a la plataforma.
            </p>
          </div>
        </div>
      </div>

      {/* Secciones de antecedentes del paciente */}
      <div className="bg-white rounded-xl border border-gray-100 p-6">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2">
            <ClipboardList size={18} className="text-primary-500" />
            <h3 className="font-semibold text-text-primary">Secciones de antecedentes</h3>
            <span className="text-xs font-medium text-text-secondary bg-gray-100 rounded-full px-2 py-0.5">
              {antecedentesEnabledCount} / {ANTECEDENTES_TOGGLEABLE.length}
            </span>
          </div>
          {canWrite && (
            <button
              type="button"
              onClick={enableAllAntecedentes}
              className="flex items-center gap-1.5 text-xs font-medium text-primary-600 hover:text-primary-700 transition-colors"
            >
              <CheckCircle2 size={13} />
              Activar todo
            </button>
          )}
        </div>
        <p className="text-sm text-text-secondary mb-4">
          Elige qué secciones del expediente (Antecedentes) aplican a tu perfil de clínica. Vacunación, alergias, patológicos,
          no patológicos y heredofamiliares siempre están disponibles.
        </p>
        <div className="divide-y divide-gray-50">
          {ANTECEDENTES_TOGGLEABLE.map(seccion => {
            const enabled = !!form.configuracionJson.antecedentesSecciones[seccion.id];
            const Icon = seccion.icon;
            return (
              <div key={seccion.id} className="flex items-center gap-4 py-3 first:pt-0 last:pb-0">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 bg-primary-50">
                  <Icon size={17} className="text-primary-500" />
                </div>
                <p className="flex-1 min-w-0 text-sm font-medium text-text-primary">{seccion.label}</p>
                <Toggle
                  enabled={enabled}
                  onChange={() => toggleAntecedenteSeccion(seccion.id)}
                  disabled={!canWrite}
                />
              </div>
            );
          })}
        </div>
      </div>

      {/* Plantillas sugeridas para el perfil de clínica */}
      {canWrite && <PlantillasSugeridasCard perfilTipo={form.perfilTipo} />}

      {/* Cobro online de teleconsultas */}
      {canWrite && <TeleconsultaOnlineToggle />}

      {/* Plantilla de columnas y etiquetas del módulo Tareas */}
      {canWrite && usuario?.clinicaId && <TareasConfigPanel />}

      {/* Exportación masiva de expedientes */}
      {canExport && (
        <div className="bg-white rounded-xl border border-gray-100 p-6">
          <div className="flex items-center gap-2 mb-1">
            <Download size={18} className="text-amber-500" />
            <h3 className="font-semibold text-text-primary">Exportar expedientes</h3>
          </div>
          <p className="text-sm text-text-secondary mb-4">
            Descarga un archivo ZIP con todos los expedientes clínicos de tu clínica en formato JSON.
            Útil para auditorías, portabilidad de datos o antes de cancelar tu cuenta.
          </p>
          <ExportacionMasivaPanel />
        </div>
      )}
    </div>
  );
}
